// ============================================================================
// HR & Hiring data hook — single source of truth for the hiring Kanban.
//
// Exposes a TanStack-Query-shaped API (data, isLoading, error, refetch,
// mutate) so a future migration to @tanstack/react-query is mechanical.
// Today the project doesn't have TanStack Query installed (see package.json),
// so we implement the same surface on top of plain useState/useEffect — the
// pattern Supply Management uses (see modules/supply-management/supply-requests
// .tsx). DECISION (Task 4): keep this internal so we don't need to add a
// dependency under a tight clock.
//
// SCHEMA RECONCILIATION (2026-05-12 evening):
//   PostgREST column aliasing (id:request_id, role_title:title) and the
//   null-stamping of non-existent description/transcript columns were
//   removed. The frontend types now match live DB schema directly. See
//   COWORK-2026-05-12-EVENING-HR-HIRING-RECONCILE-REPORT.md.
//
// RLS notes:
//   * superuser / hr / account_manager all SELECT visible rows directly;
//     no client-side scoping is needed because RLS does it.
//   * Embeds (accounts, facilities, positions) are joined via PostgREST. The
//     `facilities.account_id` FK exists as of 2026-05-04 (Task 1 fix). The
//     `positions.position_id` and `accounts.account_id` references are the
//     standard ones and have not been a problem.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type {
  HiringRequest,
  HiringRequestStatus,
  HiringUrgency,
} from '../../../shared/types';
import { errorMessage } from '../../../shared/errors';
import { commitWrite, useReader } from '../../../shared/save-path';
import { assertHourlyTierMatches } from '../facility-tiers';
import { seatsFor, type Seat } from '../seats';

// Every write in this module declares what it touched, and the board below
// registers against the same tables, so a save anywhere re-reads it (§15).
const HIRING_TABLES = ['hiring_requests'] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HiringRequestRow extends HiringRequest, Seat {
  account_name: string | null;
  facility_name: string | null;
  position_title: string | null;
  // EID of the AM who owns the facility this req is tied to. Used by the
  // "Mine" filter's OR clause (Antonela Q5): a req is "mine" if I requested
  // it OR I'm the AM on its facility. Null when facility_id is null.
  facility_account_manager_eid: string | null;
}

export interface HiringDataResult {
  data: HiringRequestRow[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
  // Optimistic status transition. Updates the local cache immediately,
  // POSTs the change, and rolls back on failure.
  updateStatus: (requestId: string, next: HiringRequestStatus) => Promise<void>;
  // Mark a request as withdrawn (requestor changed their mind, position
  // filled internally, scope changed, etc.). Sets status='Withdrawn',
  // stamps closed_date + closed_reason. Refetches on success.
  withdrawRequest: (requestId: string, reason: string) => Promise<void>;
  // Hard-delete via the admin_force_delete RPC (HR + COO only via RLS).
  // Writes an audit row to deletion_requests. Refetches on success.
  deleteRequest: (requestId: string, reason: string) => Promise<void>;
}

// New-row payload — all fields the UI collects from the form / voice flow.
// Maps to live DB columns. urgency_label is the CHECK-constrained value; we
// also compose the free-text `urgency` ("High (5)") server-side via the
// urgency_score input.
export interface NewHiringRequestInput {
  account_id: string;
  facility_id: string | null;
  position_id: string | null;
  requested_by: string; // user_profiles.id (UUID), nullable on DB but we always have one
  title: string;
  headcount: number;
  urgency_label: HiringUrgency;
  urgency_score?: number | null; // 1..5; if omitted we derive from the label
  notes: string | null; // free-text context (the form's "description" field maps here)
  original_message: string | null; // raw voice transcript or original message
  source_channel?: string | null;
}

// Default urgency_score values for each label so the free-text composite
// `urgency` column stays consistent with the data already in the table
// (e.g. "High (5)", "Medium (3)", "Low (2)").
const DEFAULT_URGENCY_SCORE: Record<HiringUrgency, number> = {
  High: 5,
  Medium: 3,
  Low: 2,
};

// ---------------------------------------------------------------------------
// Read hook — list with embeds
// ---------------------------------------------------------------------------

export function useHiringRequests(): HiringDataResult {
  const [data, setData] = useState<HiringRequestRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);

  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Keep prior values visible while we re-fetch on a manual refetch tick;
      // only flip isLoading on the initial load (lastUpdated null).
      setIsLoading((prev) => (lastUpdated === null ? true : prev));
      setError(null);
      try {
        // Flat select + parallel IN-lookups, joined client-side. No PostgREST
        // resource embedding — see docs/database-changes.md for rationale.
        const { data: flat, error: e } = await supabase
          .from('hiring_requests')
          .select(
            `request_id, account_id, facility_id, position_id, requested_by,
             title, role_type, shift, posting_source, employment_type, rate_offered,
             headcount, split_from_request_id, urgency, urgency_label, urgency_score,
             target_start_date, status, approved_by, approved_date,
             owner_position_key, assigned_to_eid, source_channel, original_message, notes,
             created_date, closed_date, closed_reason, created_at, updated_at`,
          )
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (e) throw e;
        const flatRows = (flat ?? []) as unknown as HiringRequest[];

        const accIds = Array.from(new Set(flatRows.map((r) => r.account_id).filter(Boolean))) as string[];
        const facIds = Array.from(new Set(flatRows.map((r) => r.facility_id).filter(Boolean))) as string[];
        const posIds = Array.from(new Set(flatRows.map((r) => r.position_id).filter(Boolean))) as string[];

        const [accRes, facRes, posRes] = await Promise.all([
          accIds.length === 0
            ? Promise.resolve({ data: [] as unknown[], error: null })
            : supabase.from('accounts').select('account_id, account_name').in('account_id', accIds),
          facIds.length === 0
            ? Promise.resolve({ data: [] as unknown[], error: null })
            : supabase.from('facilities').select('facility_id, facility_name, account_manager_eid').in('facility_id', facIds),
          posIds.length === 0
            ? Promise.resolve({ data: [] as unknown[], error: null })
            : supabase.from('positions').select('position_id, title').in('position_id', posIds),
        ]);
        if (cancelled) return;

        const accMap: Record<string, string | null> = {};
        for (const a of (accRes.data ?? []) as Array<{ account_id: string; account_name: string | null }>) {
          accMap[a.account_id] = a.account_name;
        }
        const facMap: Record<string, string | null> = {};
        const facMgrMap: Record<string, string | null> = {};
        for (const f of (facRes.data ?? []) as Array<{
          facility_id: string;
          facility_name: string | null;
          account_manager_eid: string | null;
        }>) {
          facMap[f.facility_id] = f.facility_name;
          facMgrMap[f.facility_id] = f.account_manager_eid;
        }
        const posMap: Record<string, string | null> = {};
        // ⛔ The column is `title`. This read asked for `position_title`,
        // which does not exist, so PostgREST refused the whole request and
        // every position name on this screen came back null — silently,
        // because the failure is tolerated below. It did not show while the
        // table was empty; seeding the night-manager positions is what makes
        // it visible. The app-facing field keeps its name.
        for (const p of (posRes.data ?? []) as Array<{ position_id: string; title: string | null }>) {
          posMap[p.position_id] = p.title;
        }

        // Counted from the WHOLE list before any filter, so hiding a sibling
        // never changes the "of 3" on the card still showing.
        const seats = seatsFor(flatRows);
        const enriched: HiringRequestRow[] = flatRows.map((r) => ({
          ...r,
          ...(seats.get(r.request_id) ?? { seat_number: null, seat_count: null }),
          account_name: r.account_id ? accMap[r.account_id] ?? null : null,
          facility_name: r.facility_id ? facMap[r.facility_id] ?? null : null,
          position_title: r.position_id ? posMap[r.position_id] ?? null : null,
          facility_account_manager_eid: r.facility_id
            ? facMgrMap[r.facility_id] ?? null
            : null,
        }));
        setData(enriched);
        setLastUpdated(new Date());
      } catch (err) {
        if (cancelled) return;
        console.error('[hr-hiring] load failed', err);
        setError(errorMessage(err, 'Failed to load hiring requests.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  // The hand-off writes employee_onboarding and the request together; either
  // one changing is a reason to re-read the board.
  useReader([...HIRING_TABLES, 'employee_onboarding'], refetch);

  const updateStatus = useCallback(
    async (requestId: string, next: HiringRequestStatus) => {
      // Snapshot for rollback.
      let prevStatus: HiringRequestStatus | null = null;
      setData((rows) =>
        rows.map((r) => {
          if (r.request_id !== requestId) return r;
          prevStatus = r.status;
          return { ...r, status: next };
        }),
      );
      try {
        await setHiringStatus(requestId, next);
      } catch (err) {
        console.error('[hr-hiring] status update failed', err);
        // Roll back the optimistic write.
        if (prevStatus !== null) {
          const rollback = prevStatus;
          setData((rows) =>
            rows.map((r) =>
              r.request_id === requestId ? { ...r, status: rollback } : r,
            ),
          );
        }
        throw err;
      }
    },
    [],
  );

  // Withdraw — soft-close: status='Withdrawn' + closed_date + closed_reason.
  // No optimistic write because the row disappears from the active board
  // (no Withdrawn column), so we just refetch.
  const withdrawRequest = useCallback(
    async (requestId: string, reason: string) => {
      const trimmed = reason.trim();
      if (!trimmed) throw new Error('Withdraw reason is required.');
      const { error: e } = await supabase
        .from('hiring_requests')
        .update({
          status: 'Withdrawn',
          // closed_date is type `date` in DB — write YYYY-MM-DD, not ISO ts.
          closed_date: new Date().toISOString().slice(0, 10),
          closed_reason: trimmed,
        })
        .eq('request_id', requestId);
      if (e) throw e;
      setTick((n) => n + 1);
    },
    [],
  );

  // Hard delete via admin_force_delete RPC. RLS on the RPC restricts to
  // hr/superuser. Writes audit row to deletion_requests.
  const deleteRequest = useCallback(
    async (requestId: string, reason: string) => {
      const trimmed = reason.trim();
      if (!trimmed) throw new Error('Delete reason is required.');
      const { error: e } = await supabase.rpc('admin_force_delete', {
        p_table_name: 'hiring_requests',
        p_record_id: requestId,
        p_reason: trimmed,
      });
      if (e) throw e;
      setTick((n) => n + 1);
    },
    [],
  );

  return useMemo(
    () => ({
      data,
      isLoading,
      error,
      lastUpdated,
      refetch,
      updateStatus,
      withdrawRequest,
      deleteRequest,
    }),
    [
      data,
      isLoading,
      error,
      lastUpdated,
      refetch,
      updateStatus,
      withdrawRequest,
      deleteRequest,
    ],
  );
}

// The stage move — the board's drag, the edit panel's "Move to stage" and
// "Mark as no longer needed" (Disregarded) — and Ask AI's hiring card, one save.
// The move to Onboarding is not this: it is the hand-off (createOnboardingFromHire).
export async function setHiringStatus(requestId: string, next: HiringRequestStatus): Promise<void> {
  await commitWrite({
    label: 'move the position to a new stage',
    write: supabase
      .from('hiring_requests')
      .update({ status: next })
      .eq('request_id', requestId)
      .select('request_id'),
    touches: HIRING_TABLES,
  });
}

// ---------------------------------------------------------------------------
// Insert helper — used by the request-form / voice-capture flow.
// ---------------------------------------------------------------------------

export async function createHiringRequest(
  input: NewHiringRequestInput,
): Promise<{ request_id: string } | null> {
  // request_id is auto-assigned by BEFORE INSERT trigger
  // (trg_hiring_requests_assign_id) as HRQ-YYYY-NNNN — see the audit report.
  // status defaults to 'Open' server-side; we don't override here so the
  // DB default stays the source of truth for new rows.
  const score =
    input.urgency_score ?? DEFAULT_URGENCY_SCORE[input.urgency_label];
  const payload = {
    account_id: input.account_id,
    facility_id: input.facility_id,
    position_id: input.position_id,
    requested_by: input.requested_by,
    title: input.title.trim(),
    headcount: input.headcount,
    urgency_label: input.urgency_label,
    urgency_score: score,
    urgency: `${input.urgency_label} (${score})`,
    notes: input.notes,
    original_message: input.original_message,
    source_channel: input.source_channel ?? 'App_Form',
  };
  // A request for N people comes back as ONE row: the database stores the
  // first seat and adds the rest itself (item 621).
  const [row] = await commitWrite<{ request_id: string }>({
    label: 'open the hiring request',
    write: supabase.from('hiring_requests').insert(payload).select('request_id'),
    touches: HIRING_TABLES,
  });
  return row ?? null;
}

// ---------------------------------------------------------------------------
// Lookup hooks — small, on-demand fetches for the form / voice-parser context.
// Kept in this file so callers have a single import surface for "hiring data".
// ---------------------------------------------------------------------------

export interface AccountOption {
  account_id: string;
  account_name: string | null;
  account_status: string | null;
}
export interface FacilityOption {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
  /** Drives tier 1 of the picker. Never filtered on — only grouped by. */
  facility_type?: string | null;
  /** Shown as a label on non-Active rows. Never filtered on. */
  status?: string | null;
}
export interface PositionOption {
  position_id: string;
  position_title: string | null;
}

export function useHiringFormOptions(): {
  accounts: AccountOption[];
  facilities: FacilityOption[];
  positions: PositionOption[];
  loading: boolean;
  error: string | null;
} {
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [accRes, facRes, posRes] = await Promise.all([
          supabase
            .from('accounts')
            .select('account_id, account_name, account_status')
            .order('account_name'),
          // 2026-05-12 repoint: hiring requests are operational — you only
          // hire for active facilities. active_facilities view excludes
          // Closed/Cancelled rows.
          //
          // 2026-09-03: `facility_type` and `status` added because the picker
          // now tiers on type (Ben's ruling). Nothing is filtered on either —
          // the view returns 211 rows (203 Active, 6 Onboarding, 2 Pending) and
          // every one of them stays reachable. `status` is shown as a small
          // label on the row so a non-Active building is visibly non-Active
          // rather than quietly missing.
          supabase
            .from('active_facilities')
            .select('facility_id, facility_name, account_id, facility_type, status')
            .order('facility_name'),
          supabase
            .from('positions')
            .select('position_id, title')
            .order('title'),
        ]);
        if (cancelled) return;
        // Positions table may be empty (per handover doc, Antonela + Amy still
        // need to seed it). Treat its specific failure as soft — the form
        // tolerates a null position_id.
        if (accRes.error) throw accRes.error;
        if (facRes.error) throw facRes.error;
        setAccounts((accRes.data ?? []) as AccountOption[]);
        setFacilities((facRes.data ?? []) as FacilityOption[]);
        if (!posRes.error) {
          // Mapped, not cast: the column is `title`, the option keeps the
          // name the form already uses.
          setPositions(
            ((posRes.data ?? []) as Array<{ position_id: string; title: string | null }>)
              .map((p) => ({ position_id: p.position_id, position_title: p.title })),
          );
        } else {
          console.warn('[hr-hiring] positions load skipped', posRes.error);
          setPositions([]);
        }

        // Tier 2 of the picker is "non-hospital buildings we actually staff
        // hourly", and the truth for that lives in the punch table. That table
        // is readable by hr, finance and executives ONLY — an account manager
        // cannot read it — so the tier list is a constant, and this is the
        // drift alarm on it: any reader who IS permitted re-derives the list
        // and shouts if the constant no longer matches.
        //
        // ⛔ The read is deliberately allowed to fail. A refusal is the
        // expected outcome for most users and means "cannot check", NOT "the
        // list is empty" — so it never becomes an empty derived list that the
        // tiering could act on. Never swallow an error to NULL (§8.8): the
        // permitted-reader case is the only one that produces a verdict.
        const punch = await supabase.from('shift_segments').select('facility_id');
        if (cancelled) return;
        if (punch.error) {
          console.info(
            '[hr-hiring] cannot verify the hourly tier from this account (punch history is hr/finance/exec only) — using the recorded list',
          );
        } else {
          const facTypeById = new Map(
            ((facRes.data ?? []) as FacilityOption[]).map((f) => [f.facility_id, f.facility_type ?? null]),
          );
          const derived = Array.from(
            new Set((punch.data ?? []).map((r) => r.facility_id as string)),
          ).filter((id) => facTypeById.get(id) !== 'Hospital');
          const drift = assertHourlyTierMatches(derived);
          if (drift) console.error(`[hr-hiring] ${drift}`);
        }
      } catch (e) {
        if (cancelled) return;
        console.error('[hr-hiring] form options failed', e);
        setError(errorMessage(e, 'Failed to load form data.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { accounts, facilities, positions, loading, error };
}

// ===========================================================================
// 2026-05-28 — Hiring Pipeline Overhaul (Antonela spec §1.3 + §3.4).
// Structured New Position create / Edit update, and the Hiring → Onboarding
// handoff that seeds an employee_onboarding row.
// ===========================================================================
// (HiringRequestStatus + HiringUrgency are imported at the top of this file.)

// Full field set the New Position modal collects (§1.3). facility_id is
// OPTIONAL — the picker is account-filtered; leaving it blank creates an
// account-wide / cross-facility requisition (facility_id NULL). Added the
// picker 2026-05-28 (fast-follow) so the "Mine" facility-AM clause works for
// newly created reqs.
export interface CreatePositionInput {
  title: string;
  account_id: string;
  facility_id: string | null;
  headcount: number;
  urgency_label: HiringUrgency;
  shift: string | null;
  posting_source: string | null;
  notes: string | null;
  status: HiringRequestStatus; // default 'Open' from the form
  requested_by: string | null; // current user's EID
}

// Editable subset for the Edit slide-over (§4.4). All optional — only the
// changed keys are sent.
//
// ⛔ No headcount. A hiring request is one seat (item 621); the only way to
// ask for another person is addOpening() below, and there is no way to lower
// it — a seat that is no longer needed is Disregarded on its own card.
export interface UpdatePositionInput {
  title?: string;
  account_id?: string;
  facility_id?: string | null;
  urgency_label?: HiringUrgency;
  shift?: string | null;
  posting_source?: string | null;
  notes?: string | null;
}

// Modal payload for the Hiring → Onboarding handoff (§3.4).
export interface HandoffInput {
  hireName: string;
  // 2026-06-02 (Antonela H3): Partner folded into Subcontractor — they mean the
  // same thing. Only W2 and Subcontractor are offered now.
  hireType: 'W2' | 'Subcontractor';
  startDate: string; // YYYY-MM-DD
  payRate: number | null; // optional; defaults to rate_offered
}

// employment_type mapping (§ HANDOFF CONTRACT). Verified against live
// employees.employment_type values: only 'W2' and 'Subcontractor' are present.
// 2026-06-02 (Antonela H3): the 'Partner' category was removed — Partner and
// Subcontractor are the same thing, so the map only carries the two live values.
export const HANDOFF_EMPLOYMENT_TYPE_MAP: Record<
  HandoffInput['hireType'],
  string
> = {
  W2: 'W2',
  Subcontractor: 'Subcontractor',
};

// Canonical onboarding checklist seed — APPENDIX A. Inserted verbatim as
// employee_onboarding.checklist_json. The companion Onboarding-module prompt
// renders against this exact shape — DO NOT DEVIATE.
export const ONBOARDING_CHECKLIST_SEED = {
  documents: [
    { key: 'offer_letter_signed', label: 'Offer letter signed', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
    { key: 'i9', label: 'I-9 completed', status: 'pending', applies_to: ['W2'] },
    { key: 'w4', label: 'W-4 completed', status: 'pending', applies_to: ['W2'] },
    { key: 'w9', label: 'W-9 completed', status: 'pending', applies_to: ['Partner', 'Subcontractor'] },
    { key: 'direct_deposit', label: 'Direct deposit form on file (link only — no bank info in DB)', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
    { key: 'handbook_signed', label: 'Employee handbook signed', status: 'pending', applies_to: ['W2'] },
  ],
  compliance: [
    { key: 'background_initiated', label: 'Background check initiated', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
    { key: 'background_cleared', label: 'Background check cleared', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
    { key: 'drug_test', label: 'Drug test (if required by account)', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
    { key: 'coi_received', label: 'COI received (subcontractors)', status: 'pending', applies_to: ['Subcontractor'] },
    { key: 'uniform_issued', label: 'Uniform issued', status: 'pending', applies_to: ['W2', 'Partner'] },
  ],
  training: [
    { key: 'osha_basics', label: 'OSHA basics completed', status: 'pending', applies_to: ['W2', 'Partner'] },
    { key: 'infection_control', label: 'Infection control / terminal cleaning training', status: 'pending', applies_to: ['W2', 'Partner'] },
    { key: 'account_specific', label: 'Account-specific training (if applicable)', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
    { key: 'first_shift_shadow', label: 'First shift shadow completed', status: 'pending', applies_to: ['W2', 'Partner'] },
  ],
  admin: [
    { key: 'added_to_scheduling', label: 'Added to scheduling system', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
    { key: 'added_to_payroll', label: 'Added to payroll (via Finance → Gusto)', status: 'pending', applies_to: ['W2'] },
    { key: 'account_assignment', label: 'Account assignment confirmed', status: 'pending', applies_to: ['W2', 'Partner', 'Subcontractor'] },
    { key: 'emergency_contact', label: 'Emergency contact on file', status: 'pending', applies_to: ['W2'] },
  ],
} as const;

const TODAY = () => new Date().toISOString().slice(0, 10);

// Create a position (structured form, no voice). request_id is trigger-
// assigned (HRQ-YYYY-NNNN); status is set explicitly so the chosen stage
// sticks rather than relying on the DB default.
export async function createPosition(
  input: CreatePositionInput,
): Promise<{ request_id: string } | null> {
  const score = DEFAULT_URGENCY_SCORE[input.urgency_label];
  const payload = {
    account_id: input.account_id,
    facility_id: input.facility_id,
    title: input.title.trim(),
    headcount: input.headcount,
    urgency_label: input.urgency_label,
    urgency_score: score,
    urgency: `${input.urgency_label} (${score})`,
    shift: input.shift?.trim() || null,
    posting_source: input.posting_source || null,
    notes: input.notes?.trim() || null,
    status: input.status,
    requested_by: input.requested_by,
    // TCSD-0162 — HR owns this by POSITION. owner_position_key defaults to 'hr';
    // nobody is assigned by name at creation. ⛔ Never write a person here.
    source_channel: 'Manual',
    created_date: TODAY(),
  };
  // A request for N people comes back as ONE row — the first seat. The
  // database opens the other N-1 itself, so this form, the voice intake and
  // anything written later all split the same way (item 621).
  const [row] = await commitWrite<{ request_id: string }>({
    label: 'create the position',
    write: supabase.from('hiring_requests').insert(payload).select('request_id'),
    touches: HIRING_TABLES,
  });
  return row ?? null;
}

// "Add another opening like this." Raising a seat's headcount by one is the
// one door: the database adds the new seat to the group, Open, and holds this
// one at 1. No second mechanism is built here on purpose (item 621).
export async function addOpening(row: Pick<HiringRequestRow, 'request_id' | 'headcount'>): Promise<void> {
  await commitWrite({
    label: 'add another opening like this',
    write: supabase
      .from('hiring_requests')
      .update({ headcount: (row.headcount ?? 1) + 1 })
      .eq('request_id', row.request_id)
      .select('request_id'),
    touches: HIRING_TABLES,
  });
}

// Patch editable fields on an existing requisition (§4.4 Edit slide-over).
export async function updatePosition(
  requestId: string,
  patch: UpdatePositionInput,
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.title !== undefined) payload.title = patch.title.trim();
  if (patch.account_id !== undefined) payload.account_id = patch.account_id;
  if (patch.facility_id !== undefined) payload.facility_id = patch.facility_id;
  if (patch.urgency_label !== undefined) {
    payload.urgency_label = patch.urgency_label;
    const score = DEFAULT_URGENCY_SCORE[patch.urgency_label];
    payload.urgency_score = score;
    payload.urgency = `${patch.urgency_label} (${score})`;
  }
  if (patch.shift !== undefined) payload.shift = patch.shift?.trim() || null;
  if (patch.posting_source !== undefined)
    payload.posting_source = patch.posting_source || null;
  if (patch.notes !== undefined) payload.notes = patch.notes?.trim() || null;
  if (Object.keys(payload).length === 0) return;
  await commitWrite({
    label: 'save the position',
    write: supabase
      .from('hiring_requests')
      .update(payload)
      .eq('request_id', requestId)
      .select('request_id'),
    touches: HIRING_TABLES,
  });
}

// The client-side EON-NNNN minter that used to live here is GONE. It read the
// newest id, added one, and inserted — so two hand-offs in the same moment both
// read EON-0002 and both tried to write EON-0003, and one of them lost the hire
// somebody had just typed in. trg_employee_onboarding_assign_id now mints it
// through next_prefixed_id, which takes a row lock and does not race.
// Insert without an id and read it back. Never mint a PK client-side.

// Hiring → Onboarding handoff (§3.4 + HANDOFF CONTRACT). Inserts an
// employee_onboarding row seeded with the canonical checklist, then advances
// the source hiring_requests row to 'Onboarding' and stamps closure fields.
// Returns the new onboarding_id for the confirmation toast deep-link.
export async function createOnboardingFromHire(
  row: HiringRequestRow,
  input: HandoffInput,
): Promise<{ onboarding_id: string }> {
  const employmentType = HANDOFF_EMPLOYMENT_TYPE_MAP[input.hireType];
  const payRateNote =
    input.payRate != null ? ` Pay rate: $${input.payRate}/hr.` : '';

  // ONE packet for ONE seat. This never reads headcount and must not start to:
  // a request is one seat, so closing this request closes exactly the seat
  // that was filled and leaves its siblings open (item 621).
  const [inserted] = await commitWrite<{ onboarding_id: string }>({
    label: 'start onboarding for the new hire',
    touches: ['employee_onboarding'],
    write: supabase
    .from('employee_onboarding')
    .insert({
      // No onboarding_id — trg_employee_onboarding_assign_id mints it.
      employee_id: null,
      hiring_request_id: row.request_id,
      position_id: row.position_id,
      start_date: input.startDate,
      employment_type: employmentType,
      status: 'Offer Accepted',
      checklist_json: ONBOARDING_CHECKLIST_SEED,
      assigned_am: row.requested_by,
      // TCSD-0162 — owned by the HR POSITION (column default), assigned to nobody.
      notes: `Auto-created from hiring request ${row.request_id}. Hire: ${input.hireName}.${payRateNote}`,
    })
    .select('onboarding_id'),
  });
  const onboardingId = inserted?.onboarding_id ?? '';

  // Advance + close the source requisition. Also persist pay rate onto
  // rate_offered when supplied (employee_onboarding has no pay-rate column).
  const reqPatch: Record<string, unknown> = {
    status: 'Onboarding',
    closed_date: TODAY(),
    closed_reason: 'Hired — onboarding initiated',
  };
  if (input.payRate != null) reqPatch.rate_offered = input.payRate;
  await commitWrite({
    label: 'move the filled seat to Onboarding',
    write: supabase
      .from('hiring_requests')
      .update(reqPatch)
      .eq('request_id', row.request_id)
      .select('request_id'),
    touches: HIRING_TABLES,
  });

  return { onboarding_id: onboardingId };
}
